import { Spin, Table } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import React from 'react';
import { statusApi } from '../../../api';
import constants from '../../../constants';
import { buildColumn, getDefaultTablePaginationConfig, ScrollType } from '../../../Helper';
import { ControllerStatusViewer, WidgetInfo } from '../../../model/StatusModel';
import './controllerStatus.scss';

// Internal state for the component
interface State {
	isLoading: boolean;
	data: ControllerStatusViewer[];
}

// Normal properties for the component
interface OwnProps {
	isAll?: boolean;
	isExpanded: boolean;
	widgetInfo: WidgetInfo;
}

// combine them together
type Props = OwnProps;

//Avoid creating object style inline, since increases reconciliations
const scroll: ScrollType = { x: 1500, y: 312 };
const pagination: TablePaginationConfig = getDefaultTablePaginationConfig();
const columns: ColumnsType<any> = [
	{
		...buildColumn(_('Name'), 'ControllerName', '10%', 'left'),
		sorter: (a, b) => a.ControllerName.localeCompare(b.ControllerName),
	},
	{
		...buildColumn(_('Address'), 'Address', '12%', 'left'),
		sorter: (a, b) => a.Address.localeCompare(b.Address),
	},
	{
		...buildColumn(_('Status'), 'Status', '7%', 'left'),
		sorter: (a, b) => (a.Status ? a.Status.localeCompare(b.Status) : ''),
	},
	{
		...buildColumn(_('ThreatLevel'), 'ThreatLevel', '7%', 'left'),
		sorter: (a, b) => (a.ThreatLevel ? a.ThreatLevel.localeCompare(b.ThreatLevel) : ''),
	},
	{
		...buildColumn(_('EnclosureTamper'), 'EnclosureTamper', '7%', 'left'),
		sorter: (a, b) => (a.EnclosureTamper ? a.EnclosureTamper.localeCompare(b.EnclosureTamper) : ''),
	},
	{
		...buildColumn(_('Enabled'), 'Enabled', '7%', 'left'),
		sorter: (a, b) => a.Enabled.localeCompare(b.Enabled),
	},
	{
		...buildColumn(_('ACPower'), 'ACPower', '7%', 'left'),
		sorter: (a, b) => (a.ACPower ? a.ACPower.localeCompare(b.ACPower) : ''),
	},
	{
		...buildColumn(_('UPSBattery'), 'UPSBattery', '7%', 'left'),
		sorter: (a, b) => (a.UPSBattery ? a.UPSBattery.localeCompare(b.UPSBattery) : ''),
	},
	{
		...buildColumn(_('MemoryBattery'), 'MemoryBattery', '7%', 'left'),
		sorter: (a, b) => (a.MemoryBattery ? a.MemoryBattery.localeCompare(b.MemoryBattery) : ''),
	},
	{
		...buildColumn(_('CCMFirmwareVersion'), 'CCMFirmware', '7%', 'left'),
		sorter: (a, b) => a.CCMFirmware.localeCompare(b.CCMFirmware),
	},
	{
		...buildColumn(_('CCMBIOSVersion'), 'CCMBIOS', '7%', 'left'),
		sorter: (a, b) => a.CCMBIOS.localeCompare(b.CCMBIOS),
	},
	{
		...buildColumn(_('SNIB2FirmwareVersion'), 'SNIB2Firmware', '7%', 'left'),
		sorter: (a, b) => a.SNIB2Firmware.localeCompare(b.SNIB2Firmware),
	},
	{
		...buildColumn(_('SNIB3FirmwareVersion'), 'SNIB3Firmware', '7%', 'left'),
		sorter: (a, b) => a.SNIB3Firmware.localeCompare(b.SNIB3Firmware),
	},
	{
		...buildColumn(_('SNIB3OSVersion'), 'SNIB3OS', '7%', 'left'),
		sorter: (a, b) => a.SNIB3OS.localeCompare(b.SNIB3OS),
	},
	{
		...buildColumn(_('SNIB3DriverVersion'), 'SNIB3Driver', '7%', 'left'),
		sorter: (a, b) => a.SNIB3Driver.localeCompare(b.SNIB3Driver),
	},
];

class ControllerStatus extends React.Component<Props, State> {
	constructor(prop: Props) {
		super(prop);

		this.state = {
			isLoading: true,
			data: null,
		};

		//We use this flag to avoid multiple api calls at the same time
		window.sessionStorage.setItem(constants.sessionStorage.statusViewer.RETRIEVE_CONTROLLER_STATUS_FLAG, '0');
	}

	componentDidMount() {
		this.fetchData();
	}

	componentWillUnmount() {
		this.clearFetchTimeout();
	}

	componentDidCatch(error, errorInfo) {
		console.log(error, errorInfo);
	}

	clearFetchTimeout() {
		if (this.autoRefreshPerformanceLoop) {
			clearTimeout(this.autoRefreshPerformanceLoop);
		}
	}

	private autoRefreshPerformanceLoop: null | ReturnType<typeof setTimeout> = null;

	shouldComponentUpdate(nextProps: Props, nextState: State) {
		const { isExpanded } = this.props;

		if (nextProps.isExpanded) {
			this.clearFetchTimeout();

			if (!isExpanded) {
				this.fetchData();
			} else {
				const { widgetInfo } = this.props;

				if (widgetInfo.autoRefresh) {
					this.autoRefreshPerformanceLoop = setTimeout(() => {
						this.fetchData();
					}, widgetInfo.autoRefreshTimer * 1000);
				}
			}
		}

		return JSON.stringify(this.state.data) !== JSON.stringify(nextState.data);
	}

	fetchData() {
		const sessionStorageKey = constants.sessionStorage.statusViewer.RETRIEVE_CONTROLLER_STATUS_FLAG;
		if (window.sessionStorage.getItem(sessionStorageKey) === '0') {
			window.sessionStorage.setItem(sessionStorageKey, '1');

			statusApi
				.retrieveControllerStatusViewer()
				.then(response => {
					window.sessionStorage.setItem(sessionStorageKey, '0');

					this.setState({
						isLoading: false,
						data: response,
					});
				})
				.catch(e => console.log(e));
		}
	}

	render() {
		const { isLoading, data } = this.state;
		const { isAll } = this.props;

		let content;
		if (!isLoading) {
			let tableData;
			if (data && data.length) {
				tableData = data.map((controller, index) => {
					return {
						key: index,
						ControllerName: controller.ControllerName,
						Address: controller.Address,
						Status: controller.Status,
						ThreatLevel: controller.ThreatLevel,
						EnclosureTamper: controller.EnclosureTamper,
						Enabled: controller.Enabled == 1 ? _('Yes') : _('No'),
						ACPower: controller.ACPower,
						UPSBattery: controller.UPSBattery,
						MemoryBattery: controller.MemoryBattery,
						CCMFirmware: controller.CCMFirmware,
						CCMBIOS: controller.CCMBIOS,
						SNIB2Firmware: controller.SNIB2Firmware,
						SNIB3Firmware: controller.SNIB3Firmware,
						SNIB3OS: controller.SNIB3OS,
						SNIB3Driver: controller.SNIB3Driver,
					};
				});
			}

			content = (
				<Table
					id='statusDashboardStatusViewerControllersTable'
					columns={columns}
					dataSource={tableData}
					scroll={scroll}
					size='small'
					className={'status-widget-table-nested'}
					pagination={pagination}
				/>
			);
		}

		return (
			<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='default' className='spin-container'>
				<div className='controller-status-container'>
					{isAll && (
						<div className='header'>
							<h3>{_('Controllers')}</h3>
						</div>
					)}
					{content}
				</div>
			</Spin>
		);
	}
}

export { ControllerStatus };
